/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { Button, makeStyles, mergeClasses, Text, tokens } from "@fluentui/react-components"
import { bundleIcon, ChevronLeftFilled, ChevronLeftRegular, ChevronRightFilled, ChevronRightRegular } from "@fluentui/react-icons";
import { Row, Column } from "../common"
import { useCommonStyles } from "../common/Styles/commonStyles";
import { useTimePeriodService } from "../../utilities/useTimePeriod";
import { useUtilities } from "../../utilities/useUtilities";

type HorizontalSelectorControlProps = {
    onClickLeft?: () => void;
    onClickRight?: () => void;
    onClickMiddle?: () => void;
    children?: React.ReactNode;
}
export const HorizontalSelectorControl = (props: HorizontalSelectorControlProps) => {

    const { onClickLeft, onClickMiddle, onClickRight, children } = props;

    //Styles
    const commonStyles = useCommonStyles();

    //Logic
    const clickLeft = (e:any) => {
        e.stopPropagation();
        
        onClickLeft && onClickLeft();
    }
    const clickRight = (e:any) => {
        e.stopPropagation();

        onClickRight && onClickRight();
    }
    const clickMiddle = (e:any) => {
        e.stopPropagation();

        onClickMiddle && onClickMiddle();
    }

    //Icons
    const ArrowLeftBundle = bundleIcon(ChevronLeftFilled, ChevronLeftRegular);
    const ArrowRightBundle = bundleIcon(ChevronRightFilled, ChevronRightRegular);

    return (
        <>
            <Row noWrap gap='small' width='auto'>
                <Column width='auto' align='right' vAlign='middle'>
                    <Button className={commonStyles.button.root}
                        appearance='subtle'
                        shape='rounded'
                        size='small'
                        onClick={clickLeft}
                        icon={<ArrowLeftBundle color={tokens.colorBrandStroke1} />}
                        data-testid='horizontal-selector-left-button'
                    />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <Button appearance='subtle' onClick={clickMiddle} data-testid='horizontal-selector-middle-button'>
                        {children}
                    </Button>
                </Column>
                <Column width='auto' align='left' vAlign='middle'>
                    <Button className={commonStyles.button.root}
                        appearance='subtle'
                        shape='rounded'
                        size='small'
                        onClick={clickRight}
                        icon={<ArrowRightBundle color={tokens.colorBrandStroke1} />}
                        data-testid='horizontal-selector-right-button'
                    />
                </Column>
            </Row>
        </>
    )
}


type HorizontalWeekDaysSelectorControlProps = {
    date?: Date;
    onSelectDate?: (date: Date) => void;
}
export const HorizontalWeekDaysSelectorControl = (props: HorizontalWeekDaysSelectorControlProps) => {

    const { date, onSelectDate } = props;

    //Hooks
    const timePeriods = useTimePeriodService();
    const utils = useUtilities();

    //State
    const [selectedDate, setSelectedDate] = useState<Date>(date || new Date());
    const [startDate, setStartDate] = useState<Date>();

    const startPlus1 = startDate ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000) : undefined;
    const startPlus2 = startDate ? new Date(startDate.getTime() + 2 * 24 * 60 * 60 * 1000) : undefined;
    const startPlus3 = startDate ? new Date(startDate.getTime() + 3 * 24 * 60 * 60 * 1000) : undefined;
    const startPlus4 = startDate ? new Date(startDate.getTime() + 4 * 24 * 60 * 60 * 1000) : undefined;
    const startPlus5 = startDate ? new Date(startDate.getTime() + 5 * 24 * 60 * 60 * 1000) : undefined;
    const startPlus6 = startDate ? new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000) : undefined;

    //Styles
    const commonStyles = useCommonStyles();

    //Logic
    const getWeek = (date: Date) => {
        const [start, _end] = timePeriods.getWeekForDate(date);
        setStartDate(start);
    }
    const clickLeft = () => {
        const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() - 7);
        onSelect(newDate);
        getWeek(newDate);
    }
    const clickRight = () => {
        const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 7);
        onSelect(newDate);
        getWeek(newDate);
    }

    const onSelect = (date: Date) => {
        setSelectedDate(date);
        !!onSelectDate && onSelectDate(date);
    }

    useEffect(() => {
        if(!!date){
            getWeek(date);
            onSelect(date);
        } else {
            getWeek(new Date());
            onSelect(new Date());
        }
    }, [date]);

    //Icons
    const ArrowLeftBundle = bundleIcon(ChevronLeftFilled, ChevronLeftRegular);
    const ArrowRightBundle = bundleIcon(ChevronRightFilled, ChevronRightRegular);

    return (
        <>
            <Row noWrap gap='small' width='auto'>
                <Column width='auto' align='right' vAlign='middle'>
                    <Button className={commonStyles.button.root}
                        appearance='subtle'
                        shape='rounded'
                        size='small'
                        onClick={clickLeft}
                        icon={<ArrowLeftBundle color={tokens.colorBrandStroke1} />}
                    />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startDate || new Date()} isSelected={utils.date.isSameDay(selectedDate, startDate || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startPlus1 || new Date()} isSelected={utils.date.isSameDay(selectedDate, startPlus1 || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startPlus2 || new Date()} isSelected={utils.date.isSameDay(selectedDate, startPlus2 || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startPlus3 || new Date()} isSelected={utils.date.isSameDay(selectedDate, startPlus3 || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startPlus4 || new Date()} isSelected={utils.date.isSameDay(selectedDate, startPlus4 || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startPlus5 || new Date()} isSelected={utils.date.isSameDay(selectedDate, startPlus5 || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='center' vAlign='middle'>
                    <DateButton date={startPlus6 || new Date()} isSelected={utils.date.isSameDay(selectedDate, startPlus6 || new Date())} onSelect={onSelect} />
                </Column>
                <Column width='auto' align='left' vAlign='middle'>
                    <Button className={commonStyles.button.root}
                        appearance='subtle'
                        shape='rounded'
                        size='small'
                        onClick={clickRight}
                        icon={<ArrowRightBundle color={tokens.colorBrandStroke1} />}
                    />
                </Column>
            </Row>
        </>
    )
}

const useDateButtonStyles = makeStyles({
    root: {
        cursor: 'pointer',
        backgroundColor: tokens.colorNeutralBackground2,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
        }
    },
    selected: {
        backgroundColor: tokens.colorBrandBackground2,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2,
        }
    }
});

type DateButtonProps = {
    date: Date;
    isSelected?: boolean;
    onSelect?: (date: Date) => void;
}
const DateButton = (props: DateButtonProps) => {

    const { date, isSelected, onSelect } = props;
    const utils = useUtilities();

    //Styles
    const styles = useDateButtonStyles();

    const mainClasses = mergeClasses(
        styles.root,
        isSelected && styles.selected
    )

    return (
        <Column flex='1'
            className={mainClasses}
            subtleBorder
            borderRadius='10px'
            noGap
            align="center"
            width='2.5rem' height='3rem'
            onClick={() => !!onSelect && onSelect(date)}            
        >
            <Text size={100} weight='semibold'>{utils.date.formatDate(date, { weekday: 'short' }, true)}</Text>
            <Text size={200} weight='bold'>{utils.date.formatDate(date, { day: 'numeric' }, true)}</Text>
            <Text size={100} weight='semibold'>{utils.date.formatDate(date, { month: 'short' }, true)}</Text>
        </Column>
    )
}